import OrganizationMemberDetailsModel from '@shared/models/organizations/OrganizationMemberDetailsModel';

/**
 * Part 1 of the upgrade flow: where does upgrade CTA send user?
 */

export const UPGRADE_ENTRY_PATH = '/upgrade';
export const ORGANIZATIONS_PATH = '/org';

export interface UpgradeIntent {
    organizationId?: string;
    managedOrgId?: string;
    productId?: string;
}

interface IResolveUpgradeDestinationProps {
    memberships: OrganizationMemberDetailsModel[];
    intent?: UpgradeIntent;
}

export const canManageUpgrade = (membership: OrganizationMemberDetailsModel) =>
    Boolean(membership.isAdmin || membership.canManageBilling);

const isManagedOrg = (membership: OrganizationMemberDetailsModel) =>
    Boolean(membership.organization.originatedByOrganizationId);

/** Link target for any upgrade CTA. */
export const getUpgradeEntryPath = (intent?: UpgradeIntent) => {
    const params = new URLSearchParams();
    if (intent?.organizationId) params.set('organizationId', intent.organizationId);
    if (intent?.managedOrgId) params.set('managedOrgId', intent.managedOrgId);
    if (intent?.productId) params.set('productId', intent.productId);

    const query = params.toString();
    return query.length > 0 ? `${UPGRADE_ENTRY_PATH}?${query}` : UPGRADE_ENTRY_PATH;
};

/** Reads back what `getUpgradeEntryPath` wrote. */
export const parseUpgradeIntent = (searchParams: URLSearchParams): UpgradeIntent => ({
    organizationId: searchParams.get('organizationId') ?? undefined,
    managedOrgId: searchParams.get('managedOrgId') ?? undefined,
    productId: searchParams.get('productId') ?? undefined,
});

export const getOrganizationUpgradeRedirectPath = ({
    organizationId,
    managedOrgId,
    productId,
}: UpgradeIntent & { organizationId: string }) => {
    if (managedOrgId) {
        return `/org/${organizationId}/managed/${managedOrgId}/billing/redirect/upgrade`;
    }

    const route = `/org/${organizationId}/billing/redirect/upgrade`;
    return productId ? `${route}/product/${productId}` : route;
};

export const resolveUpgradeDestination = ({
    memberships,
    intent = {},
}: IResolveUpgradeDestinationProps): string | null => {
    const { organizationId, managedOrgId, productId } = intent;

    const requestedMembership = organizationId
        ? memberships.find((membership) => membership.organizationId === organizationId)
        : undefined;
    // With a single membership there is nothing to disambiguate, so use it even without an intent.
    const membership = requestedMembership ?? (memberships.length === 1 ? memberships[0] : undefined);

    if (!membership) {
        // More than one org and no usable hint: make the user pick one first.
        return memberships.length > 1 ? ORGANIZATIONS_PATH : null;
    }

    // `managedOrgId` means this org is acting as the billing parent, so it keeps its own route.
    if (!managedOrgId && isManagedOrg(membership)) {
        return ORGANIZATIONS_PATH;
    }

    return getOrganizationUpgradeRedirectPath({
        organizationId: membership.organizationId,
        managedOrgId,
        productId,
    });
};
